import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  alpha,
  useTheme,
  useMediaQuery,
} from '@mui/material';

const SaveConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  dealName,
  changes = [],
  fullScreen = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:1200px)');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#0f172a',
          color: 'white',
          fontWeight: 600,
          fontSize: '18px',
          py: 2.5,
          borderBottom: theme.palette.mode === 'dark' ? `1px solid ${theme.palette.divider}` : '1px solid #1e293b'
        }}
      >
        Confirm Save Deal
      </DialogTitle>
      <DialogContent sx={{ mt: 3, mb: 2 }}>
        <DialogContentText sx={{ color: theme.palette.text.primary, fontSize: '15px' }}>
          Are you sure you want to save the deal <strong>"{dealName}"</strong>?
        </DialogContentText>
        <DialogContentText sx={{ mt: 2, color: theme.palette.text.secondary, fontSize: '14px' }}>
          This will save all:
        </DialogContentText>
        <Box component="ul" sx={{ mt: 1, pl: 3, color: theme.palette.text.secondary }}>
          <li>Deal data</li>
          <li>Cashflow data</li>
          <li>Analytics data</li>
          <li>ADCO data (if available)</li>
        </Box>

        {changes && changes.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <DialogContentText sx={{ color: theme.palette.text.secondary, fontSize: '14px', mb: 1 }}>
              Changes to CF_INPUTS_RAW:
            </DialogContentText>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <Box component="thead">
                <Box component="tr" sx={{ textAlign: 'left', color: theme.palette.text.secondary }}>
                  <Box component="th" sx={{ py: 0.5, pr: 2 }}>Tranche</Box>
                  <Box component="th" sx={{ py: 0.5, pr: 2 }}>Field</Box>
                  <Box component="th" sx={{ py: 0.5, pr: 2 }}>Current</Box>
                  <Box component="th" sx={{ py: 0.5 }}>New</Box>
                </Box>
              </Box>
              <Box component="tbody">
                {changes.map((c, i) => (
                  <Box component="tr" key={`${c.tranche}-${c.field}-${i}`} sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
                    <Box component="td" sx={{ py: 0.5, pr: 2 }}>{c.tranche}</Box>
                    <Box component="td" sx={{ py: 0.5, pr: 2 }}>{c.field}</Box>
                    <Box component="td" sx={{ py: 0.5, pr: 2, color: theme.palette.text.secondary }}>
                      {c.oldVal === undefined || c.oldVal === null || c.oldVal === '' ? '—' : String(c.oldVal)}
                    </Box>
                    <Box component="td" sx={{ py: 0.5, fontWeight: 600 }}>{String(c.newVal)}</Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.mode === 'dark'
            ? '#0f172a'
            : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          p: 3,
          gap: 2,
          justifyContent: 'flex-end',
          flexDirection: isMobile ? 'column-reverse' : 'row'
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          fullWidth={isMobile}
          sx={{
            borderColor: theme.palette.text.secondary,
            color: theme.palette.text.primary,
            fontWeight: 600,
            borderRadius: 2,
            px: isMobile ? 3 : 4,
            py: 2,
            textTransform: 'none',
            fontSize: isMobile ? '16px' : '14px',
            minWidth: isMobile ? 'auto' : 120,
            minHeight: isMobile ? 56 : 'auto',
            '&:hover': {
              borderColor: theme.palette.text.primary,
              bgcolor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.paper, 0.8)
                : alpha(theme.palette.primary.main, 0.04),
              transform: 'translateY(-1px)'
            },
            transition: 'all 0.2s ease'
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="primary"
          fullWidth={isMobile}
          sx={{
            fontWeight: 600,
            borderRadius: 2,
            px: isMobile ? 3 : 4,
            py: 2,
            textTransform: 'none',
            fontSize: isMobile ? '16px' : '14px',
            minWidth: isMobile ? 'auto' : 160,
            minHeight: isMobile ? 56 : 'auto',
            boxShadow: '0 4px 8px rgba(2, 132, 199, 0.3)',
            '&:hover': {
              bgcolor: '#0369a1',
              transform: 'translateY(-1px)',
              boxShadow: '0 6px 12px rgba(2, 132, 199, 0.4)'
            }
          }}
        >
          Save Deal
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaveConfirmDialog;


